import axios from 'axios';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { getCurrentWindowPath, persistAuthRedirectPath } from './authRedirect';

/**
 * Global API interceptor for handling JWT expiry errors.
 *
 * Strategy:
 *  1. On a 401 with "jwt expired", attempt a silent token refresh via
 *     `auth.currentUser.getIdToken(true)` and retry the original request once.
 *  2. If the retry also fails (or there is no current user), sign out and
 *     redirect to the login page.
 *
 * This covers both `axios` calls and raw `fetch` calls used across the app.
 */

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────

/** Check whether a response body (string) contains the jwt-expired signal. */
const isJwtExpiredBody = (body) => {
  if (typeof body !== 'string') return false;
  return body.includes('jwt expired');
};

let isLoggingOut = false;

const forceLogout = async () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  try {
    if (auth.currentUser) {
      await signOut(auth);
    }
  } catch (_) {
    // Best-effort
  }
  persistAuthRedirectPath(getCurrentWindowPath());
  window.location.href = '/auth/login';
};

let isRefreshing = false;
let refreshPromise = null;

/**
 * Try to get a fresh Firebase ID token.
 * Returns the new token string, or null if it cannot be refreshed.
 * Prevents multiple concurrent refresh requests if multiple APIs fail simultaneously.
 */
const refreshToken = async () => {
  try {
    if (!auth.currentUser) return null;

    if (isRefreshing && refreshPromise) {
      return await refreshPromise;
    }

    isRefreshing = true;
    // `true` forces a refresh from Firebase servers
    refreshPromise = auth.currentUser.getIdToken(true);
    const token = await refreshPromise;

    isRefreshing = false;
    refreshPromise = null;
    return token;
  } catch (_) {
    isRefreshing = false;
    refreshPromise = null;
    return null;
  }
};

// ──────────────────────────────────────────────
// Axios interceptor
// ──────────────────────────────────────────────
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401s that look like jwt-expired, and only retry once.
    if (error.response?.status === 401 && !originalRequest._jwtRetried) {
      const data = error.response?.data;
      const bodyText = typeof data === 'string' ? data : JSON.stringify(data ?? '');

      if (isJwtExpiredBody(bodyText)) {
        const newToken = await refreshToken();
        if (newToken) {
          originalRequest._jwtRetried = true;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(originalRequest);
        }
        // Could not refresh → force logout
        forceLogout();
      }
    }

    return Promise.reject(error);
  },
);

// ──────────────────────────────────────────────
// Fetch interceptor
// ──────────────────────────────────────────────
const originalFetch = window.fetch;

window.fetch = async function patchedFetch(...args) {
  const response = await originalFetch.apply(this, args);

  if (response.status !== 401) {
    return response;
  }

  // Clone so downstream consumers can still read the original body.
  const clone = response.clone();
  let bodyText;
  try {
    bodyText = await clone.text();
  } catch (_) {
    return response; // Can't read → return as-is
  }

  if (!isJwtExpiredBody(bodyText)) {
    return response;
  }

  // Attempt silent refresh
  const newToken = await refreshToken();
  if (!newToken) {
    forceLogout();
    return response;
  }

  // Rebuild the request with the fresh token.
  const [input, init = {}] = args;
  const newHeaders = new Headers(init.headers || {});
  newHeaders.set('Authorization', `Bearer ${newToken}`);
  const retryInit = {
    ...init,
    headers: Object.fromEntries(newHeaders.entries()),
  };

  // Retry once
  const retryResponse = await originalFetch.call(this, input, retryInit);

  if (retryResponse.status === 401) {
    // Still failing → give up and force logout
    const retryClone = retryResponse.clone();
    try {
      const retryBody = await retryClone.text();
      if (isJwtExpiredBody(retryBody)) {
        forceLogout();
      }
    } catch (_) {
      // ignore
    }
  }

  return retryResponse;
};
